import React from "react";
import "../../styles/layout.scss"; // Import the SCSS file for styling
import BookingHeader from "./BookingHeader.tsx";
import useWindowWidthThreshold from "../hooks/useWindowWidthThreshold";

/**
 * TEMPORARY: hides the maroon BookingHeader on the post-booking flows
 * (secure-your-booking, place-your-order, manage-your-order, /confirm).
 *
 * While the header is hidden, those flows must keep the step indicator and
 * Back button on mobile, so the desktop BookingStepHeader is rendered on all
 * breakpoints via `stepHeaderWrapperClass`. Flows that still want the header
 * (e.g. /book) opt out with the `keepHeaderWhenHidden` prop.
 *
 * To restore the previous behaviour: delete `HIDE_HEADER`,
 * `stepHeaderWrapperClass`, the `keepHeaderWhenHidden` prop, and their usages.
 */
export const HIDE_HEADER: boolean = true;

/**
 * Wrapper class for BookingStepHeader. When the header is hidden the desktop
 * step/back header must also show on mobile, so the `hidden lg:block`
 * restriction is dropped.
 */
export const stepHeaderWrapperClass = HIDE_HEADER ? "" : "gcw:hidden gcw:lg:block";

interface LayoutProps {
  showHeader?: boolean;
  /** Keep the BookingHeader even while {@link HIDE_HEADER} is enabled (e.g. /book). */
  keepHeaderWhenHidden?: boolean;
  headerChildren?: React.ReactNode;
  main: React.ReactNode;
  aside: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
  mainClassName?: string;
  asideClassName?: string;
  venueInfo?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({
  showHeader = false,
  keepHeaderWhenHidden = false,
  headerChildren,
  main,
  aside,
  footer,
  venueInfo,
  mainClassName = "",
  asideClassName = "",
  footerClassName = "",
}) => {
  const isDesktop = useWindowWidthThreshold(1024);

  return (
    <>
      {showHeader && (!HIDE_HEADER || keepHeaderWhenHidden) && <BookingHeader>{headerChildren}</BookingHeader>}
      <div
        id="gc-layout"
        className={[
          aside || venueInfo ? "app-layout" : "no-sidebar app-layout",
          venueInfo ? "booking-journey-layout" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {venueInfo && !isDesktop && (
          <div id="gc-venue-info-mobile" className="venueInfoMobile gcw:pb-6 gcw:bg-white">
            {venueInfo}
          </div>
        )}
        <main id="gc-main" className={`gc-main ${mainClassName}`}>
          {main}
        </main>

        {(aside || venueInfo) && (
          <aside id="gc-aside" className={`gc-aside ${asideClassName}`}>
            {venueInfo && isDesktop && (
              <div id="gc-venue-info-desktop" className="venueInfoDesktop gcw:mb-6">
                {venueInfo}
              </div>
            )}
            {aside && <div id="gc-aside-content">{aside}</div>}
          </aside>
        )}
      </div>
      {footer && (
        <footer id="gc-footer" className={`gc-footer ${footerClassName}`}>
          {footer}
        </footer>
      )}
    </>
  );
};

export default Layout;
